/**
 * Волна C (docs/MIGRATION_ROUTER.md §5). Было: <PrivateRoute path='/recipes/:id'>.
 * было не-exact; Recipe получает match.params через Layout-Route
 */
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import CacheDataRoute from '../../CacheDataRoute';
import Recipe from '@routes/Recipe/component';
import { getQueryClient } from '../../providers/query';
import { parseRecipeUrlId, recipeByIdQueryOptions } from '@queries/index';
import { isContentNotFoundError } from '../../api/contentNotFound';

/**
 * ЭТАП 3 п.3 — КАНОНИЗАЦИЯ АДРЕСА РЕЦЕПТА (301).
 *
 * В проде у рецепта ровно один адрес — `slug-id`, и LazarServer это
 * поддерживал двумя правилами (`lib/routes/recipes.ts`): голый числовой
 * `/recipes/123` -> 301 на `/recipes/<identify>-123`, а «почти правильный»
 * slug (опечатка, старое имя после переименования, самодельная ссылка) ->
 * 301 на список `/recipes`. У нас же `parseRecipeUrlId` берёт хвостовое
 * число и рендерит рецепт при ЛЮБОМ префиксе — то есть внешний сайт мог
 * наплодить сколько угодно живых адресов одной страницы. Разбор — в
 * `docs/SSR_LAZARSERVER_AUDIT.md`, п.3.
 *
 * Данные тут уже есть (их ждёт честный 404), лишнего запроса нет.
 *
 * ПОЛЕ `identify` ДОБАВЛЕНО В ЗАПРОС `getRecipeById` (см.
 * api/gql/query/recipes.cartEndpoint.ts) — до перегенерации codegen
 * (`npm run codegen`) его нет в типах, поэтому читаем через узкий каст, а
 * не через `any`: отсутствие поля (старый ответ, пустая строка) просто
 * означает «канонизировать не во что», и адрес остаётся как есть.
 */
const canonicalRecipeRedirect = (
  urlSegment: string,
  recipeId: number,
  data: unknown
): void => {
  // getQueryData отдаёт СЫРОЙ ответ запроса: `select` из queryOptions
  // применяется только к результату useQuery, здесь его нет
  const identify = (
    data as { getRecipeById?: { identify?: string | null } } | undefined
  )?.getRecipeById?.identify;
  if (!identify) {
    // У рецепта нет slug — канонический адрес и есть числовой, как в проде
    return;
  }
  const canonical = `${identify}-${recipeId}`;
  if (urlSegment === canonical) {
    return;
  }
  if (/^\d+$/.test(urlSegment)) {
    // Голый id: склеиваем со slug-адресом
    throw redirect({ to: `/recipes/${canonical}`, statusCode: 301 });
  }
  // Чужой slug: в проде такой адрес уводили на список раздела, а не на
  // «правильный» рецепт — повторяем, чтобы не плодить входов на страницу
  throw redirect({ to: '/recipes', statusCode: 301 });
};

export const Route = createFileRoute('/recipes/$id')({
  /**
   * ЭТАП 2 — честный 404 несуществующего рецепта: бэк (recipeUtil
   * .getRecipeById) кидает CONTENT_NOT_FOUND -> notFound() -> HTTP 404 +
   * контент /404 без редиректа. Битый хвост (id не число) — 404 сразу:
   * раньше компонент делал history.replace('/404') уже на клиенте.
   * Лоадеры TanStack параллельны root-префетчу -> await ensureQueryData
   * (дедуп по ключу). Клиентскую навигацию не блокируем.
   */
  loader: async ({ params }) => {
    const urlSegment = decodeURIComponent(params.id);
    const recipeId = parseRecipeUrlId(urlSegment);
    if (!Number.isFinite(recipeId)) {
      throw notFound();
    }
    if (typeof window !== 'undefined') {
      return;
    }
    const queryClient = getQueryClient();
    const options = recipeByIdQueryOptions(recipeId);
    // Префетч успел упасть раньше — ошибка уже в кэше, без второго запроса
    const settled = queryClient.getQueryState(options.queryKey);
    if (settled?.error) {
      if (isContentNotFoundError(settled.error)) {
        throw notFound();
      }
      return;
    }
    try {
      await queryClient.ensureQueryData(options);
    } catch (error) {
      if (isContentNotFoundError(error)) {
        throw notFound();
      }
      return;
    }
    canonicalRecipeRedirect(
      urlSegment,
      recipeId,
      queryClient.getQueryData(options.queryKey)
    );
  },
  component: () => <CacheDataRoute component={Recipe} path='/recipes/:id' />
});
